/**
 * Keeps cart quantities inside the rules the subscription builder enforces.
 *
 * Rebuy's drawer renders raw Shopify lines with a free-running +/- stepper. Its own limit
 * is a single static per-line min/max for the whole cart, which can't express our rules:
 * the subscription cap is a TOTAL across lines, and it differs by bag size (300g allows 4,
 * 1 Kilo allows 3 — see SIZE_OPTIONS in subscriptionBuilder.svelte).
 *
 * The rule is a TOTAL across all subscription lines, sized by the bags in the cart:
 *   • any 300g bag present → up to 4 bags
 *   • all bags 1 Kilo      → up to 3 bags
 *
 * Subscription lines ONLY. One-time add-ons and ordinary products are deliberately left
 * alone — buying six bags in one order is a legitimate sale, and neither carries a selling
 * plan whose bag count could be contradicted.
 *
 * Only the upper bound is enforced. A decrease is a legitimate "I want fewer bags" signal;
 * the builder reflects it as "3 of 4 selected" and asks the customer to finish step 2.
 */

export function isSubscriptionLine(line) {
  return !!((line || {}).properties || {})._rc_bundle;
}

function isKiloLine(line) {
  const size = `${(line.properties || {})._Size || ''} ${line.variant_title || ''}`;
  return /kilo/i.test(size);
}

/**
 * Bags the plan holds — the bag count chosen in step 1, written onto every subscription line
 * as `_Bags` at add time. Being on the line means it survives removals (unlike summing the
 * surviving `_Size` values, which shrank the ceiling and stranded freed bags) and works in
 * any browser (unlike localStorage).
 *
 * Carts added before `_Bags` existed fall back to the size ceiling: 4 if any 300g bag is
 * present, 3 if they're all 1 Kilo.
 */
export function subscriptionPlanBags(cart) {
  const lines = ((cart && cart.items) || []).filter(isSubscriptionLine);
  if (!lines.length) return 0;

  for (let i = 0; i < lines.length; i += 1) {
    const bags = parseInt((lines[i].properties || {})._Bags, 10);
    if (Number.isFinite(bags) && bags > 0) return bags;
  }

  return lines.every(isKiloLine) ? 3 : 4;
}

function subscriptionTotal(cart) {
  return ((cart && cart.items) || [])
    .filter(isSubscriptionLine)
    .reduce((sum, line) => sum + line.quantity, 0);
}

/**
 * Plan bags minus bags in the cart. One number drives all three rules:
 *
 *   > 0  short of the plan  → Rebuy's `+` stays live, and checkout is blocked
 *   = 0  exactly right      → `+` disabled, checkout allowed
 *   < 0  over the plan      → trimmed back by enforceCartQuantityRules
 */
export function subscriptionHeadroom(cart) {
  return subscriptionPlanBags(cart) - subscriptionTotal(cart);
}

/**
 * Trims the subscription back to the cap when the TOTAL is exceeded.
 *
 * A total, not a per-line rule: a 4-bag plan built as Araponga Drip ×1 + Araponga Espresso ×1
 * + Robinson ×2 must still let the customer drop Robinson to 1 and put that bag on Araponga.
 * Per-line clamping blocked that redistribution and contradicted the `+` button, which is
 * also total-based.
 *
 * Returns the corrected cart, or null if nothing needed changing.
 */
export async function enforceCartQuantityRules(cart) {
  const lines = ((cart && cart.items) || []).filter(isSubscriptionLine);
  if (!lines.length) return null;

  const allowed = subscriptionPlanBags(cart);
  let excess = subscriptionTotal(cart) - allowed;
  if (excess <= 0) return null;

  // Take the overage off the largest lines first — that's where the customer just pushed the
  // quantity up. Never below 1: removing a coffee entirely is what the trash button is for.
  const updates = {};
  lines
    .slice()
    .sort((a, b) => b.quantity - a.quantity)
    .forEach(line => {
      if (excess <= 0) return;
      const reducible = Math.min(excess, line.quantity - 1);
      if (reducible > 0) {
        updates[line.key] = line.quantity - reducible;
        excess -= reducible;
      }
    });

  if (!Object.keys(updates).length) return null;

  console.info(
    `[Coava] Subscription total exceeded its ${allowed}-bag plan; trimming. `
      + 'Bag count is set in step 1 of the subscription builder.',
    updates,
  );

  try {
    const res = await fetch('/cart/update.js', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body:    JSON.stringify({ updates }),
    });
    if (!res.ok) return null;
    return res.json();
  } catch (err) {
    console.warn('[Coava] could not correct cart quantities:', err);
    return null;
  }
}
